import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Button } from "@/components-v2/Button";
import { Icon } from "@/components-v2/Icon";
import { LabelledDivider } from "@/components-v2/LabelledDivider";
import { Tag } from "@/components-v2/Tag";
import { SsoAccountCandidate, SsoAccountSelection, SsoLoginMethodType } from "@/types/UserInfo";
import { TMessageKey } from "@/i18n/types.gen";
import { TFunction, TMessage } from "@/i18n/tt";
import { formatDate } from "@/i18n/dateFormat";
import { Language } from "@/i18n/languages";
import { logError } from "@/utils/log";
import { buttonRoleProps } from "@/utils/events";
import BankIDSymbol from "@/assets/img/BankIDSymbol.svg";
import BankIDSymbolDark from "@/assets/img/BankIDSymbolDark.svg";
import BankIDSwedenLogo from "@/assets/img/BankIDSwedenLogo.svg";
import BankIDSwedenLogoDark from "@/assets/img/BankIDSwedenLogoDark.svg";
import MitIDDenmark from "@/assets/img/MitIdDenmarkLogo.svg";
import MitIDDenmarkDark from "@/assets/img/MitIdDenmarkLogoDark.svg";
import FinlandFlag from "@/assets/img/FinlandFlag.svg";
import {
  ContentBox,
  LoginHeader,
  LoginLayout,
  LoginMethodIcon,
  LoginMethodLogo,
} from "./loginComponents";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { isProductionEnvironment, isTestEnvironment } from "@/utils/environments";
import { compact } from "lodash";

const loginMethodTranslationKey: Record<SsoLoginMethodType, TMessageKey> = {
  BANKID_NORWAY: "ssoAccountSelection.method.bankidNorway",
  BANKID_SWEDEN: "ssoAccountSelection.method.bankidSweden",
  MITID_DENMARK: "ssoAccountSelection.method.mitidDenmark",
  BANKID_FINLAND: "ssoAccountSelection.method.bankidFinland",
  BANKID: "common.bankid",
  USERNAME_PASSWORD: "ssoAccountSelection.method.usernamePassword",
  MICROSOFT: "ssoAccountSelection.method.microsoft",
  GOOGLE: "ssoAccountSelection.method.google",
};

type PageStep = "select" | "verify";

const bankIdMethods: [id: string, href: string, icon: LoginMethodIcon, label: TMessage][] = compact(
  [
    [
      "bankid-no",
      "/api/auth/bankid",
      { src: BankIDSymbol, darkSrc: BankIDSymbolDark },
      { key: "loginWithBankidNorway" },
    ],
    [
      "bankid-se",
      "/api/auth/bankid-sweden",
      { src: BankIDSwedenLogo, darkSrc: BankIDSwedenLogoDark },
      { key: "loginWithBankidSweden" },
    ],
    [
      "mitid-dk",
      "/api/auth/mitid",
      { src: MitIDDenmark, darkSrc: MitIDDenmarkDark },
      { key: "loginWithMitIdDenmark" },
    ],
    !isProductionEnvironment() &&
      !isTestEnvironment() && [
        "ftn-fi",
        "/api/auth/ftn",
        { src: FinlandFlag },
        { key: "loginWithFinnishFtn" },
      ],
  ],
);

const domainNotActivatedError = "domain-not-activated";
const workspaceAccountRequiredError = "workspace-account-required";

const SsoAccountSelectionPage = observer(function SsoAccountSelectionPage() {
  const { apiService } = useRootStore();
  const { t, lang } = useTranslation();
  const queryParams = new URLSearchParams(window.location.search);
  const error = queryParams.get("error");
  const domain = queryParams.get("domain")?.trim() ?? "";
  const blockingError =
    error === domainNotActivatedError
      ? {
          title: "sso.domain-not-activated.title" as const,
          intro: t("sso.domain-not-activated.description", { domain }),
        }
      : error === workspaceAccountRequiredError
        ? {
            title: "sso.workspace-account-required.title" as const,
            intro: t("sso.workspace-account-required.description"),
          }
        : null;
  const hasBlockingError = blockingError !== null;
  const [data, setData] = useState<SsoAccountSelection | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<PageStep>("select");

  useEffect(() => {
    if (hasBlockingError) {
      return;
    }
    apiService
      .fetchSsoCandidates()
      .then(setData)
      .catch((err) => {
        logError("Failed to fetch SSO candidates", err);
        location.replace("/login");
      })
      .finally(() => setLoading(false));
  }, [apiService, hasBlockingError]);

  const handleSelect = (externalId: string | null) => {
    if (submitting) return;
    setSubmitting(true);
    apiService
      .confirmSsoSelection(externalId)
      .then((result) => {
        if (result.redirectTo) {
          location.replace(result.redirectTo);
        } else {
          setStep("verify");
        }
      })
      .catch((err) => {
        logError("SSO account confirmation failed", err);
        setSubmitting(false);
      });
  };

  if (blockingError) {
    return (
      <LoginLayout onBack={() => location.replace("/login")}>
        <LoginHeader heading={{ key: blockingError.title }} intro={blockingError.intro} />
      </LoginLayout>
    );
  }

  if (loading || !data) {
    return (
      <LoginLayout>
        <LoadingIndicator />
      </LoginLayout>
    );
  }

  return (
    <LoginLayout
      onBack={() => {
        if (step === "verify") {
          setStep("select");
        } else {
          location.replace("/login");
        }
      }}
    >
      {step === "select" && (
        <SelectStep data={data} lang={lang} submitting={submitting} onSelect={handleSelect} t={t} />
      )}
      {step === "verify" && <VerifyStep data={data} t={t} />}
    </LoginLayout>
  );
});

const SelectStep = observer(function SelectStep({
  data,
  lang,
  submitting,
  onSelect,
  t,
}: {
  data: SsoAccountSelection;
  lang: Language;
  submitting: boolean;
  onSelect: (externalId: string | null) => void;
  t: TFunction;
}) {
  if (data.candidates.length === 0) {
    return (
      <VBox gap={"lg"}>
        <LoginHeader
          heading={{ key: "ssoAccountSelection.title", params: { provider: data.provider } }}
          intro={t("ssoAccountSelection.no-account.subtitle", {
            email: data.email,
          })}
        />
        <ContentBox>
          <Button
            variant="secondary"
            label={t("ssoAccountSelection.createNew")}
            onClick={() => onSelect(null)}
            disabled={submitting}
            fullWidth
          />
        </ContentBox>
      </VBox>
    );
  }
  return (
    <>
      <LoginHeader
        heading={{ key: "ssoAccountSelection.title", params: { provider: data.provider } }}
        intro={t("ssoAccountSelection.subtitle", {
          count: String(data.candidates.length),
          email: data.email,
        })}
      />
      {data.candidates.length > 0 && (
        <ContentBox gap={"sm"}>
          <VBox gap={"md"}>
            {data.candidates.map((candidate) => (
              <AccountCard
                key={candidate.externalId}
                candidate={candidate}
                lang={lang}
                disabled={submitting}
                onClick={() => onSelect(candidate.externalId)}
              />
            ))}
          </VBox>
        </ContentBox>
      )}

      <LabelledDivider label={{ key: "or" }} />
      <ContentBox>
        <Button
          variant="secondary"
          label={t("ssoAccountSelection.createNew")}
          onClick={() => onSelect(null)}
          disabled={submitting}
          fullWidth
        />
      </ContentBox>
    </>
  );
});

const VerifyStep = observer(function VerifyStep({
  data,
  t,
}: {
  data: SsoAccountSelection;
  t: (key: TMessageKey, params?: Record<string, string>) => string;
}) {
  return (
    <>
      <ContentBox gap={"sm"}>
        <h2 className="text-h3">{t("ssoAccountSelection.verify.title")}</h2>
        <p>{t("ssoAccountSelection.verify.subtitle", { provider: data.provider })}</p>
      </ContentBox>

      <ContentBox gap={"md"}>
        {bankIdMethods.map(([id, href, icon, label]) => (
          <BankIdButton key={id} id={id} href={href} icon={icon} label={label} />
        ))}
      </ContentBox>
    </>
  );
});

const BankIdButton = observer(function BankIdButton({
  id,
  href,
  icon,
  label,
}: {
  id: string;
  href: string;
  icon: LoginMethodIcon;
  label: TMessage;
}) {
  const { t } = useTranslation();
  return (
    <a
      id={id}
      href={href}
      className="group flex rounded border p-4 no-underline
        font-semibold border-primary color-primary
        hover:border-primary-dark hover:color-primary-dark"
    >
      <Grid grow alignItems="center">
        <GridItem
          colSpan={3}
          justifySelf={"center"}
          className="opacity-subtle group-hover:opacity-full"
        >
          <LoginMethodLogo icon={icon} alt={t(label)} />
        </GridItem>
        <GridItem colSpan={9}>{t(label)}</GridItem>
      </Grid>
    </a>
  );
});

const AccountCard = observer(function AccountCard({
  candidate,
  lang,
  disabled,
  onClick,
}: {
  candidate: SsoAccountCandidate;
  lang: Language;
  disabled: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      border
      rounded={"lg"}
      padding={"lg"}
      alignItems={"center"}
      justifyContent={"space-between"}
      gap={"md"}
      hoverBg
      className="hover:border-primary cursor-pointer"
      htmlProps={disabled ? { "aria-disabled": true } : buttonRoleProps(onClick)}
    >
      <VBox gap={"sm"}>
        <Box fontWeight={"bold"}>{candidate.name}</Box>
        <Box alignItems={"center"}>
          <span className="subtle">{candidate.email ?? candidate.name}</span>
          <Tag
            label={
              candidate.isEmailVerified
                ? t("ssoAccountSelection.verified")
                : t("ssoAccountSelection.notVerified")
            }
            color={candidate.isEmailVerified ? "success" : "error"}
          />
        </Box>
        {candidate.loginMethods.length > 0 && (
          <VBox textSize={"sm"} fg={"subtle"} marginTop={"sm"}>
            <div>{t("ssoAccountSelection.existingMethods")}</div>
            <div className="content">
              <ul>
                {candidate.loginMethods.map((m) => (
                  <li key={m.method}>
                    {t(loginMethodTranslationKey[m.method])}
                    {m.lastUsed && (
                      <span className="subtle">
                        {" "}
                        {t("ssoAccountSelection.lastUsed", {
                          date: formatDate(lang, m.lastUsed),
                        })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </VBox>
        )}
      </VBox>
      <Icon name={"caret-right"} color={"subtle"} />
    </Box>
  );
});

export default SsoAccountSelectionPage;
